import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./StockEntry.css"; // Your custom CSS for styling
import { ColDef, ITooltipParams } from "ag-grid-community";
import { itemGetListType } from "../../Const/type";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../Redux/CommonSlice";
import { CustomMessage, Loader } from "../../Const/Spinner";
import { RootState } from "../../Redux/store/ReduxStore";
import { enqueueSnackbar } from "notistack";
import { createStockData, getListOfItemData } from "../../Const";
import { TextField, Select, MenuItem, InputLabel, FormControl, Button, Paper, Typography, Box, Divider } from "@mui/material";
import CustomNumberEditor from "./CustomNumberEditor";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import QRCodeReader from "../../Component/QRCodeReader/QRCodeReader";
import {
    MDBCard,
    MDBCardBody,
} from 'mdb-react-ui-kit';

const inputUnit = ["rolls", "meter", "nos", "kg"];
const StockEntry: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state:RootState)=>state.auth.user.id);
  const loading = useSelector((state: RootState) => state.common.data.loading);
  
  const [action, setAction] = useState<string>("Inward");
  const [comments, setComments] = useState<string>("");
  const gridApiRef = useRef<any>(null);
  const [itemList, setItemList] = useState<itemGetListType[]>([]);
  const [info, setInfo] = useState<string>('Double click to edit the new qty.');
  const [selectedPlant,setSelectedPlant] = useState("--Select--");
  const [listPlant,setListPlant] = useState([
    "--Select--",
    "Plant1","Plant2"]);

  const handlePlantChange = (event: any) => {
    setSelectedPlant(event.target.value);
    getItemListData(event.target.value);
  };


  const getItemListData = async (selectedPlantData:string) => {
    if(selectedPlantData === "--Select--" || selectedPlantData === ""){
      CustomMessage("Select the plant",'error',enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await getListOfItemData(selectedPlantData);
      let { data, status } = response;
      if (status.code === 200 && data?.length > 0) {
        setItemList(data);
      } else {
        CustomMessage(status.message, 'error', enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, 'error', enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const validateInput = (value: any) => {
    // const regex = /^[0-9]+$/;
    // return regex.test(value);
    return /^[+]?\d*\.?\d+$/.test(value); // Allows positive integers and decimals
  };

  const handleCellValueChanged = (params: any) => {
    if (!validateInput(params.newValue) && params?.colDef?.field === 'newQty') {
      params.node.setDataValue(params.column.getId(), params.oldValue);
      CustomMessage('Please enter a valid positive number.', 'error', enqueueSnackbar);
    } else {
      // Update the fieldEdit field to true if the value is edited
      params.node.setDataValue('fieldEdit', true);
    }
  };
    const isMobile = window.innerWidth <= 768;
  const toolTipValueGetter = (params: ITooltipParams) => params.value == null || params.value === '' ? '- Missing -' : params.value;
  const columnDefs: ColDef[] = [
    {
      headerName: "Edited",
      field: "fieldEdit",
      editable:false,
      flex:0.1
    },
    {
      headerName: "Item - Qty",
      field: "itemName",
      filter: "agSetColumnFilter",
      sortable: true,
      editable: false,
      autoHeight: isMobile ? true : false,
      cellStyle: { whiteSpace: 'normal' },
      valueFormatter:(Params)=>`${Params.data?.itemName} - ${Params.data?.stockQty}`,
      flex: isMobile ? 1 : 0.5,
    },
    {
      headerName: "New Qty",
      field: "newQty",
      filter: "agSetColumnFilter",
      sortable: true,
      autoHeight: isMobile ? true : false,
      cellStyle: { whiteSpace: 'normal' },
      editable: true,
      tooltipValueGetter: toolTipValueGetter,
      tooltipField: "newQty",
      flex: isMobile ? 1 : 0.4,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        useFormatter: true,
        formatter: (value: any) => {
          return isNaN(value) ? '' : value;
        },
        cellEditor: CustomNumberEditor,
        onCellValueChanged: handleCellValueChanged,
      },
    },
    {
    headerName: "Gross Weight",
    field: "grossWeight",
    filter: "agNumberColumnFilter",
    sortable: true,
    editable: true,
    flex: isMobile ? 1 : 0.4,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      useFormatter: true,
      formatter: (value: any) => (isNaN(value) ? '' : value),
    },
  },
  {
    headerName: "Net Weight",
    field: "netWeight",
    filter: "agNumberColumnFilter",
    sortable: true,
    editable: true,
    flex: isMobile ? 1 : 0.4,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      useFormatter: true,
      formatter: (value: any) => (isNaN(value) ? '' : value),
    },
  },
  {
    headerName: "Unit",
    field: "unit",
    sortable: true,
    editable: true,
    flex: isMobile ? 1 : 0.4,
    cellEditor: 'agSelectCellEditor',
    cellRenderer: DropdownRenderer, // Custom dropdown renderer
    cellEditorParams: {
      values: inputUnit, // Fixed this
    },
    valueFormatter: (params: any) => {
      return params.value || "";
    },
  },
  ];

  const handleActionChange = (event: any) => {
    setAction(event.target.value);
  };

  const handleCommentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComments(event.target.value);
  };

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      // minWidth: 100,
      filter: true,
    };
  }, []);

  const getRowId = useMemo(() => {
    return (params: any) => params?.data?.itemId;
  }, []);

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.api.sizeColumnsToFit();
    }
  }, []);

  const handleClick = async () => {
    // Check if any newQty is not a number
    const invalidItems = itemList.filter(item => isNaN(item.newQty));

    if (invalidItems.length > 0) {
      CustomMessage('Enter the valid number','error',enqueueSnackbar);
      return; // Prevent further action
    }

      // Additional validation for "Outward" action
    if (action === "Outward") {
      const invalidOutwardItems = itemList.filter(item => 
        item.newQty < 0 || // Prevent negative quantities
        item.stockQty === 0 && item.newQty > 0 || // Prevent newQty if stockQty is 0
        item.newQty > item.stockQty // Prevent newQty greater than available stockQty
      );

      if (invalidOutwardItems.length > 0) {
        CustomMessage('Cannot submit: New quantity cannot be negative, exceed the available stock, or be greater than 0 when stock quantity is 0 for Outward action', 'error', enqueueSnackbar);
        return; // Prevent further action
      }
    }
    if(itemList.length === 0){
      CustomMessage("data not found",'error',enqueueSnackbar);
      return;
    }
    const invalidUnits = itemList.filter(item => item.fieldEdit && !item.unit);
    if (invalidUnits.length > 0) {
      CustomMessage('Unit cannot be empty for any item.', 'error', enqueueSnackbar);
      return; // Prevent further action
    }
    
    if(itemList.filter(item => item.fieldEdit).length === 0){
      CustomMessage("No rows edited",'error',enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    const payload = {
      plant: selectedPlant,
      data: itemList.filter(item => item.fieldEdit),
      comments: comments,
      updatedBy: userId,
      action: action
    }

    console.log(payload,'payload');
    try{
      const response = await createStockData(payload);
      if(response?.status.code === 200){
        CustomMessage(response?.status?.message,'success',enqueueSnackbar);
        setComments("");
        getItemListData(selectedPlant);
      }else{
        CustomMessage(response?.status.message,'error',enqueueSnackbar);
      }
    }catch(error:any){
      CustomMessage(error?.message,'error',enqueueSnackbar);
    }finally{
      dispatch(setLoading(false));
    }
  } 
  const onScan = (val: any) => {
    // Validate if the value matches the format "X-Y" where X and Y are numbers (X can be a 1 or more digits, Y can also be a 1 or more digits)
    const regex = /^\d{1,}-\d{1,}-\d{1,}-\d{1,}-[a-zA-Z]+$/;

    if (regex.test(val)) {
      // Split the value into two parts using '-'
      const [itemId, scannedQty, grossWeight, netWeight, unit] = val.split('-');
      const itemIdNumber = parseInt(itemId, 10);
      const scannedQtyNumber = parseFloat(scannedQty);

      // Find the item by itemId
      const updatedItemList = [...itemList].map(item => {
        if (item.itemId == itemIdNumber) {
          // If item is found, update the newQty
          const updatedNewQty = item.newQty ? item.newQty + scannedQtyNumber : scannedQtyNumber;
            return { 
            ...item, 
            newQty: updatedNewQty, 
            fieldEdit: true, 
            unit, 
            netWeight: parseFloat(netWeight), 
            grossWeight: parseFloat(grossWeight) 
            };
        }
        return item;
      });

      // Update the state with the modified itemList
      setItemList(updatedItemList);
      console.log(updatedItemList)
    } else {
      CustomMessage('Invalid format. Please use "itemId-scannedQty-grossWeight-netWeight-unit" format','error',enqueueSnackbar);
    }
  };

  return (
    <div className="stock-entry-container">
      <h3 className="fw-bold mb-0 text-uppercase">Stock</h3>
      {loading && <Loader />}
      <MDBCard className="">
        <MDBCardBody className="text-white p-3 p-md-5">
          <div className="d-block d-md-flex justify-content-between gap-3">
            <FormControl className="plant-dropdown mb-3" fullWidth>
              <InputLabel id="plantSelect-label">Select Plant</InputLabel>
              <Select
                labelId="plantSelect-label"
                id="plantSelect"
                value={selectedPlant}
                label="Select Plant"
                onChange={handlePlantChange}
              >
                {listPlant.map((plant) => (
                  <MenuItem key={plant} value={plant}>{plant}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="action-dropdown mb-3" fullWidth>
              <InputLabel id="actionSelect-label">Select Action</InputLabel>
              <Select
                labelId="actionSelect-label"
                id="actionSelect"
                value={action}
                className="w-100"
                label="Select the Action"
                onChange={handleActionChange}
              >
                {["Inward","Outward"].map(val=>(
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
      {
        itemList?.length>0 && (
          <div className="camera-scan mb-3 mt-2">
            {/* <CameraAltOutlinedIcon/> */}
            <QRCodeReader onScan={onScan}/>
          </div>
        )
      }
      <Paper elevation={3} className="py-2 bg-white shadow-none">
        <Box>
          <Typography className="font-10 mb-1 fw-medium text-secondary d-flex align-items-center"><InfoOutlinedIcon className="text-theme me-1 font-14"/> {info}</Typography>
        </Box>
      </Paper>
      <div className="ag-theme-alpine ag-grid-container mb-4" style={{ width: '100%' }}>
        <AgGridReact
          className="ag-theme-alpine"
          defaultColDef={defaultColDef}
          rowData={itemList}
          columnDefs={columnDefs}
          onGridReady={(params) => (gridApiRef.current = params)}
          domLayout="normal"
          onCellValueChanged={handleCellValueChanged}
          pagination={false}
          paginationPageSize={20}
          getRowId={getRowId}
          suppressMenuHide={false}
          context={{ inputUnit }} // Pass itemList in context
        />
      </div>

      <TextField
        id="standard-multiline-static"
        type="text"
        className="comments-input mb-3"
        rows={3}
        multiline
        value={comments}
        onChange={handleCommentsChange}
        label="Comments"
        placeholder="Enter comments..."
        fullWidth
      />

      <Button className="btn-shadow" onClick={handleClick}>Submit</Button>

      </MDBCardBody>
    </MDBCard>
  </div>
  );
};

// Custom Dropdown Renderer
const DropdownRenderer = (params: any) => {
  const { inputUnit } = params?.context; // Access inputUnit from context
  const { unit } = params?.data;

  const handleChange = (event: any) => {
    params.node.setDataValue(params.colDef.field, event.target.value);
  };

  return (
    <Select value={params.value || ""} onChange={handleChange} fullWidth>
      {inputUnit?.map((option: string, i: number) => (
        <MenuItem key={i} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

export default StockEntry;